import * as React from "react";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import { createMachine, assign } from "xstate";
import { useMachine } from "@xstate/react";
import { fromCallback } from "xstate/actors";
import { useMapState, useSinkState } from "context/";
import ApiAnnotations from "api/annotations";
import { useAnnotationActions } from "context/";
import { drawStyles } from "utils/mapping/drawStyles";

const DrawingStateContext = React.createContext<any>({});
const DrawingActionsContext = React.createContext<any>({});

const VALID_MODES = ["select", "marker", "linestring", "polygon"] as const;

const createInvoke = {
  src: "create",
  input: ({ context }: any) => ({
    mapRef: context.mapRef,
    createAnnotationRef: context.createAnnotationRef,
  }),
};

const drawingMachine = createMachine(
  {
    id: "drawingController",
    initial: "loading",
    context: {
      mode: null as null | (typeof VALID_MODES)[number],
      activeDrawingLayer: null as any,
      mapRef: null as any,
      drawRef: null as any,
      createAnnotationRef: null as any,
      surveyId: undefined as string | undefined,
    },
    states: {
      loading: {
        on: {
          LOADED: {
            target: "select",
            actions: assign({
              mapRef: ({ event }) => (event as any).mapRef,
              drawRef: ({ event }) => (event as any).drawRef,
              createAnnotationRef: ({ event }) =>
                (event as any).createAnnotationRef,
            }),
          },
        },
      },

      select: {
        entry: [assign({ mode: (_c) => "select" as const }), "applyMode"],
      },

      marker: {
        entry: [assign({ mode: (_c) => "marker" as const }), "applyMode"],
      },

      linestring: {
        entry: [assign({ mode: (_c) => "linestring" as const }), "applyMode"],
        invoke: createInvoke,
        on: {
          COMPLETE: "select",
        },
      },

      polygon: {
        entry: [assign({ mode: (_c) => "polygon" as const }), "applyMode"],
        invoke: createInvoke,
        on: {
          COMPLETE: "select",
        },
      },
    },

    on: {
      SET_SURVEY_ID: {
        actions: assign({
          surveyId: ({ event }) => (event as any).id,
        }),
      },

      GOTO_SELECT: ".select",
      GOTO_MARKER: ".marker",
      GOTO_LINESTRING: ".linestring",
      GOTO_POLYGON: ".polygon",

      SET_DRAWING_LAYER: {
        actions: "setDrawingLayer",
      },
      SET_DRAWING_LAYER_IMMEDIATE: {
        actions: "setDrawingLayer",
      },

      CLEAR_DRAWING_LAYER: {
        target: ".select",
        actions: "clearDrawingLayer",
      },

      CLEANUP: ".loading",
    },
  },
  {
    // v5: actions & actors live in the second argument
    actions: {
      applyMode: ({ context }) => {
        const modeMap: Record<string, string> = {
          select: "simple_select",
          marker: "simple_select",
          linestring: "draw_line_string",
          polygon: "draw_polygon",
        };
        const draw = context.drawRef?.current;
        const target = modeMap[context.mode ?? "select"] ?? "simple_select";
        if (draw && draw.getMode() !== target) {
          draw.changeMode(target);
        }
      },

      setDrawingLayer: assign({
        activeDrawingLayer: ({ event }) => (event as any).layer || null,
      }),

      clearDrawingLayer: assign({
        activeDrawingLayer: (_ctx, _ev) => null,
      }),
    },

    actors: {
      create: fromCallback(({ sendBack, input }) => {
        const { mapRef, createAnnotationRef } = input as any;
        if (!mapRef) return;
        const modeHandler = () => sendBack({ type: "COMPLETE" });
        const createHandler = () => {
          createAnnotationRef.current?.();
        };
        mapRef.on("draw.create", createHandler);
        mapRef.on("draw.modechange", modeHandler);
        return () => {
          mapRef.off("draw.create", createHandler);
          mapRef.off("draw.modechange", modeHandler);
        };
      }),
    },
  }
);

export const DrawingProvider = ({ children, surveyId }) => {
  const { select, setDrawingMode } = useAnnotationActions();

  const drawRef = React.useRef(
    new MapboxDraw({ displayControlsDefault: false, styles: drawStyles })
  );
  const { map: mapRef, loaded } = useMapState();
  const { selectedSurveyId } = useSinkState();

  // we'll stash the function on a mutable ref the actor can call
  const createAnnotationRef = React.useRef<() => void>();

  const [state, send] = useMachine(drawingMachine, {
    context: { surveyId },
  });

  React.useEffect(() => {
    setDrawingMode(state.context.mode);
  }, [state.context.mode, setDrawingMode]);

  // keep latest implementation available to the actor
  React.useEffect(() => {
    createAnnotationRef.current = () => {
      const ctx = state.context;
      const { features } = ctx.drawRef.current.getAll();

      ApiAnnotations.createAnnotation(
        ctx.activeDrawingLayer && ctx.activeDrawingLayer.id,
        {
          label:
            features[0].geometry.type.replace("LineString", "Line") ||
            "New Annotation",
          description: "",
          geometry: JSON.stringify(features[0].geometry),
        },
        ctx.surveyId
      ).then((response) => {
        ctx.drawRef.current.deleteAll();
        select(response.id);
        send({ type: "SET_DRAWING_LAYER_IMMEDIATE", layer: response.layer });
      });
    };
  });

  React.useEffect(() => {
    send({ type: "SET_SURVEY_ID", id: surveyId });
  }, [surveyId, send]);

  React.useEffect(() => {
    if (mapRef && loaded) {
      const control = drawRef.current;
      mapRef.addControl(control);
      send({ type: "LOADED", mapRef, drawRef, createAnnotationRef });
      return () => {
        mapRef.removeControl(control);
      };
    }
  }, [mapRef, loaded, surveyId, send]);

  React.useEffect(() => {
    if (selectedSurveyId === null && state.context.drawRef) {
      send({ type: "GOTO_SELECT" });
      send({ type: "CLEAR_DRAWING_LAYER" });
    }
  }, [selectedSurveyId, state.context.drawRef, send]);

  const actions = {
    setMode: (mode: string) => {
      if (!VALID_MODES.includes(mode as any)) {
        console.error(
          `⚠️ Map Toolbar cannot set draw tools to mode <<${mode}>>. Valid modes are: ${VALID_MODES}`
        );
      }
      send({ type: `GOTO_${mode.toUpperCase()}` as any });
    },
    setActiveDrawingLayer: (layer) => {
      send({ type: "SET_DRAWING_LAYER", layer });
    },
    setActiveDrawingLayerImmediate: (layer) => {
      send({ type: "SET_DRAWING_LAYER_IMMEDIATE", layer });
    },
    clearActiveDrawingLayer: (layer) => {
      if (layer === state.context.activeDrawingLayer) {
        send({ type: "CLEAR_DRAWING_LAYER" });
      }
    },
    toggleActiveDrawingLayer: (layer) => {
      if (layer === state.context.activeDrawingLayer) {
        send({ type: "CLEAR_DRAWING_LAYER" });
      } else {
        send({ type: "SET_DRAWING_LAYER", layer });
      }
    },
  };

  return (
    <DrawingStateContext.Provider value={state.context}>
      <DrawingActionsContext.Provider value={actions}>
        {children}
      </DrawingActionsContext.Provider>
    </DrawingStateContext.Provider>
  );
};

export const useDrawingState = () => {
  const context = React.useContext(DrawingStateContext);
  if (context === undefined) {
    throw new Error("Must 'useDrawingState' within a DrawingProvider.");
  }
  return context;
};

export const useDrawingActions = () => {
  const context = React.useContext(DrawingActionsContext);
  if (context === undefined) {
    throw new Error("Must 'useDrawingState' within a DrawingProvider.");
  }
  return context;
};
